import React from "react";
import { CheckCircle2, Info, AlertCircle } from "lucide-react";

/**
 * @typedef {Object} NotificationCardItem
 * @property {string} id
 * @property {string} title
 * @property {string} body
 * @property {boolean} read
 * @property {string} created_at - ISO date string
 * @property {'success'|'warning'|'error'|'info'} [kind]
 */

/**
 * @typedef {Object} NotificationCardProps
 * @property {NotificationCardItem} notification
 * @property {(id: string) => void} [onMarkRead] - if provided and the item is unread, shows an inline mark-read action (NotificationsBell dropdown pattern)
 * @property {'absolute'|'relative'} [timeFormat] - 'relative' requires the caller to pre-format created_at via date-fns, since this component has no date-fns dependency of its own
 * @property {React.ReactNode} [formattedTime] - pre-formatted time string/node (used when timeFormat="relative")
 * @property {boolean} [showKindIcon]
 * @property {boolean} [compact] - tighter dropdown-style spacing (NotificationsBell) vs full-list spacing (Notifications page)
 */

const KIND_ICON = {
  success: <CheckCircle2 className="h-4 w-4 text-accent" aria-hidden="true" />,
  warning: <AlertCircle className="h-4 w-4 text-amber-500" aria-hidden="true" />,
  error: <AlertCircle className="h-4 w-4 text-destructive" aria-hidden="true" />,
  info: <Info className="h-4 w-4 text-primary" aria-hidden="true" />,
};

/** @param {NotificationCardProps} props */
export default function NotificationCard({
  notification,
  onMarkRead,
  formattedTime,
  showKindIcon = false,
  compact = false,
}) {
  const n = notification;

  if (compact) {
    return (
      <div className={`px-4 py-3 border-b border-border ${n.read ? "opacity-60" : ""}`} data-testid={`notif-${n.id.slice(0, 6)}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="text-sm font-medium">{n.title}</div>
          {!n.read && onMarkRead && (
            <button onClick={() => onMarkRead(n.id)} className="text-primary" title="Mark read" aria-label="Mark as read">
              <CheckCircle2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>
        <div className="text-[10px] text-muted-foreground mt-1">{formattedTime ?? new Date(n.created_at).toLocaleString()}</div>
      </div>
    );
  }

  return (
    <div className={`p-4 flex items-start gap-3 ${!n.read ? "bg-primary/5" : ""}`}>
      {showKindIcon && (KIND_ICON[n.kind] || KIND_ICON.info)}
      <div className="flex-1">
        <div className="font-semibold text-sm">{n.title}</div>
        <div className="text-sm text-muted-foreground mt-0.5">{n.body}</div>
        <div className="text-xs text-muted-foreground mt-1">{formattedTime ?? new Date(n.created_at).toLocaleString()}</div>
      </div>
    </div>
  );
}
